/**
 * MRZScanner — Component scansione MRZ client-side via Tesseract.js
 *
 * Privacy-first: l'immagine non lascia MAI il dispositivo.
 *  • Camera flusso → canvas in memoria
 *  • Crop sulla zona MRZ (porzione inferiore del frame)
 *  • Tesseract.recognize con whitelist `A-Z0-9<` + dataset `eng`
 *  • Parser ICAO 9303 → campi auto-compilati nel form
 *  • Subito dopo: canvas.width=0, video.srcObject.getTracks().stop()
 *    (Ciao ciao foto, qui non vi vogliamo dopo l'invio 🗑️)
 *
 * Errore OCR → log con placeholder *** (mai dati reali).
 *
 * iOS handling: Chrome/Edge/Firefox su iOS girano su WebKit ma `getUserMedia`
 * spesso fallisce per restrizioni di permessi. Su iOS preferiamo il flusso
 * `<input type="file" capture="environment">` che apre la camera nativa
 * iOS — affidabile su TUTTI i browser iOS.
 */
import { useEffect, useRef, useState } from 'react';
import { Camera, X, ArrowsClockwise, CheckCircle, Warning, ShieldCheck, ImageSquare } from '@phosphor-icons/react';
import Tesseract from 'tesseract.js';
import { parseMRZ } from '../lib/mrzParser';

const MRZ_WHITELIST = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<';

// iOS detection (covers iPhone/iPad incl. iPadOS in desktop mode)
const IS_IOS = typeof navigator !== 'undefined' && (
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);
// On iOS, only Safari has fully reliable getUserMedia. Chrome/Edge/Firefox iOS
// run on WebKit but often fail permission flow → prefer file input.
const IS_NON_SAFARI_IOS = IS_IOS && !/Version\/[\d.]+ Safari/.test(navigator.userAgent || '');


export default function MRZScanner({ open, onClose, onParsed, t, locale }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [status, setStatus] = useState('idle'); // idle | starting | streaming | reading | success | error
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [funMessage, setFunMessage] = useState('');

  const FUN_MESSAGES = {
    it: ['Sto leggendo il barcode... 🔍', 'Decifrazione in corso... 🤓', 'Quasi fatto... 🚀', 'Allineamento ICAO 9303... 📑'],
    en: ['Reading barcode... 🔍', 'Decoding... 🤓', 'Almost there... 🚀', 'Aligning ICAO 9303... 📑'],
    fr: ['Lecture du code... 🔍', 'Décodage... 🤓', 'Presque fini... 🚀', 'Alignement ICAO... 📑'],
    es: ['Leyendo código... 🔍', 'Decodificando... 🤓', 'Casi listo... 🚀', 'Alineando ICAO... 📑'],
    de: ['Code wird gelesen... 🔍', 'Dekodierung... 🤓', 'Fast fertig... 🚀', 'ICAO-Ausrichtung... 📑'],
  };

  // Rotate fun message during OCR
  useEffect(() => {
    if (status !== 'reading') return;
    const msgs = FUN_MESSAGES[locale] || FUN_MESSAGES.en;
    let i = 0;
    setFunMessage(msgs[0]);
    const id = setInterval(() => {
      i = (i + 1) % msgs.length;
      setFunMessage(msgs[i]);
    }, 1400);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, locale]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      stopCamera();
      setStatus('idle');
      setErrorMsg('');
      setProgress(0);
    }
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async () => {
    setStatus('starting');
    setErrorMsg('');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorMsg("Il tuo browser non supporta l'accesso alla fotocamera. Usa il bottone 'Carica foto' qui sotto.");
      setStatus('error');
      return;
    }
    // Try environment first (rear camera on phones), fallback to any camera
    const constraintsList = [
      { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false },
      { video: { facingMode: 'environment' }, audio: false },
      { video: true, audio: false },
    ];
    let stream = null;
    let lastErr = null;
    for (const c of constraintsList) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(c);
        break;
      } catch (e) {
        lastErr = e;
      }
    }
    if (!stream) {
      console.warn('camera permission ***', lastErr?.name);
      const errorByName = {
        NotAllowedError: IS_IOS
          ? 'Permesso fotocamera negato. Su iPhone vai in Impostazioni → Safari/Chrome → Camera → Consenti, poi ricarica questa pagina. Oppure usa "Carica foto" qui sotto.'
          : 'Permesso fotocamera negato. Controlla le impostazioni del browser (icona lucchetto in alto).',
        NotFoundError: 'Nessuna fotocamera trovata su questo dispositivo.',
        NotReadableError: 'La fotocamera è già in uso da un\'altra app. Chiudi le altre app e riprova.',
        OverconstrainedError: 'Configurazione fotocamera non supportata.',
        SecurityError: 'Accesso fotocamera bloccato (richiesto HTTPS).',
        AbortError: 'Accesso fotocamera annullato.',
      };
      setErrorMsg(errorByName[lastErr?.name] || `Fotocamera non disponibile (${lastErr?.name || 'errore sconosciuto'}). Usa "Carica foto" qui sotto.`);
      setStatus('error');
      return;
    }
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      try {
        await videoRef.current.play();
      } catch (_) { /* autoplay can be blocked */ }
    }
    setStatus('streaming');
  };

  // Fallback: process an image picked from the device gallery via <input type=file>
  const onFilePicked = async (file) => {
    if (!file) return;
    setStatus('reading');
    setProgress(0);
    setErrorMsg('');
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { data } = await Tesseract.recognize(dataUrl, 'eng', {
        logger: m => {
          if (m.status === 'recognizing text') setProgress(Math.round(m.progress * 100));
        },
        tessedit_char_whitelist: MRZ_WHITELIST,
      });
      const parsed = parseMRZ(data.text || '');
      if (!parsed) {
        setStatus('error');
        setErrorMsg(t('scan_error'));
        return;
      }
      setStatus('success');
      setTimeout(() => onParsed(parsed), 500);
    } catch (e) {
      console.warn('OCR file fail ***');
      setStatus('error');
      setErrorMsg(t('scan_error'));
    }
  };

  const captureAndRead = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      setErrorMsg('Stream video non pronto');
      return;
    }

    // Crop to bottom 35% of the frame (MRZ zone). Slightly wider than ICAO
    // recommends, to be forgiving with hand-held framing.
    const cropTop = Math.floor(h * 0.6);
    const cropH = h - cropTop;
    canvas.width = w;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d');
    // Boost contrast for better OCR
    ctx.filter = 'grayscale(1) contrast(1.4) brightness(1.05)';
    ctx.drawImage(video, 0, cropTop, w, cropH, 0, 0, w, cropH);

    setStatus('reading');
    setProgress(0);
    try {
      const dataUrl = canvas.toDataURL('image/png');

      // Tesseract recognize with MRZ whitelist
      const { data } = await Tesseract.recognize(dataUrl, 'eng', {
        logger: m => {
          if (m.status === 'recognizing text') setProgress(Math.round(m.progress * 100));
        },
        // Force Tesseract to consider only MRZ chars
        tessedit_char_whitelist: MRZ_WHITELIST,
      });

      // 🗑️ Ciao ciao foto, qui non vi vogliamo dopo l'invio
      canvas.width = 0;
      canvas.height = 0;

      const parsed = parseMRZ(data.text || '');
      if (!parsed) {
        setStatus('error');
        setErrorMsg(t('scan_error'));
        return;
      }

      setStatus('success');
      stopCamera();
      // Defer to give the UI a beat
      setTimeout(() => onParsed(parsed), 600);
    } catch (e) {
      console.warn('OCR fail ***'); // Privacy: never log real data
      setStatus('error');
      setErrorMsg(t('scan_error'));
      // Make sure we still wipe the canvas on failure
      canvas.width = 0;
      canvas.height = 0;
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" data-testid="mrz-scanner">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-5 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck size={20} weight="bold" className="text-violet-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-wider text-violet-300">{t('privacy_no_image').slice(0, 80)}</p>
            <p className="text-sm font-black truncate">{t('scan_doc_title')}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white p-2" data-testid="mrz-close">
          <X size={20} weight="bold" />
        </button>
      </div>

      {/* Subtitle */}
      <p className="px-4 sm:px-5 text-xs text-white/70 mb-3 leading-snug max-w-xl">{t('scan_doc_subtitle')}</p>

      {/* Camera viewport */}
      <div className="flex-1 relative overflow-hidden bg-zinc-950">
        {status === 'idle' || status === 'starting' ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6">
            {IS_NON_SAFARI_IOS ? (
              // iOS Chrome/Edge/Firefox: native file picker is more reliable
              // than getUserMedia → make IT primary.
              <>
                <label className="cursor-pointer bg-violet-600 hover:bg-violet-700 text-white font-black uppercase tracking-wider text-sm px-8 py-4 flex items-center gap-2 max-w-full" data-testid="mrz-file-label-primary">
                  <Camera size={18} weight="bold" />
                  <span className="truncate">{t('scan_start')}</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => onFilePicked(e.target.files?.[0])}
                    className="hidden"
                    data-testid="mrz-file-input-primary"
                  />
                </label>
                <p className="text-[10px] text-white/60 text-center max-w-xs leading-relaxed px-2">
                  Su iPhone tocca per aprire la fotocamera nativa o scegli una foto dalla galleria.
                </p>
                <span className="text-white/40 text-[10px] uppercase tracking-wider">— oppure —</span>
                <button
                  onClick={startCamera}
                  disabled={status === 'starting'}
                  className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-6 py-3 disabled:opacity-50 flex items-center gap-2 border border-white/20"
                  data-testid="mrz-start-secondary"
                >
                  <ArrowsClockwise size={13} weight="bold" />
                  Avvia fotocamera (sperimentale)
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={startCamera}
                  disabled={status === 'starting'}
                  className="bg-violet-600 hover:bg-violet-700 text-white font-black uppercase tracking-wider text-sm px-8 py-4 disabled:opacity-50 flex items-center gap-2 max-w-full"
                  data-testid="mrz-start"
                >
                  <Camera size={18} weight="bold" />
                  <span className="truncate">{status === 'starting' ? '...' : t('scan_start')}</span>
                </button>
                <span className="text-white/40 text-[10px] uppercase tracking-wider">— oppure —</span>
                <label className="cursor-pointer bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-6 py-3 transition-colors flex items-center gap-2 border border-white/20 max-w-full" data-testid="mrz-file-label">
                  <ImageSquare size={14} weight="bold" />
                  <span className="truncate">Carica foto del documento</span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => onFilePicked(e.target.files?.[0])}
                    className="hidden"
                    data-testid="mrz-file-input"
                  />
                </label>
                <p className="text-[10px] text-white/50 text-center max-w-xs px-2">
                  Su computer senza fotocamera, scatta una foto del documento col telefono e caricala qui.
                </p>
              </>
            )}
          </div>
        ) : null}

        <video
          ref={videoRef}
          playsInline
          muted
          className={`absolute inset-0 w-full h-full object-cover ${status === 'streaming' || status === 'reading' ? 'opacity-100' : 'opacity-0'}`}
        />

        {/* MRZ guide overlay */}
        {(status === 'streaming' || status === 'reading') && (
          <>
            <div className="absolute inset-0 pointer-events-none">
              {/* Top mask */}
              <div className="absolute inset-x-0 top-0 h-[60%] bg-black/60" />
              {/* Bottom mask area for MRZ */}
              <div className="absolute inset-x-0 bottom-0 h-[5%] bg-black/60" />
              {/* MRZ frame */}
              <div className="absolute inset-x-4 top-[60%] bottom-[5%] border-2 border-violet-400 border-dashed">
                <div className="absolute -top-7 left-0 text-xs text-violet-300 font-mono tracking-wider">MRZ &lt;&lt;&lt;&lt;</div>
              </div>
            </div>
          </>
        )}

        {/* Reading overlay */}
        {status === 'reading' && (
          <div className="absolute inset-x-0 bottom-20 px-4 text-center">
            <div className="bg-violet-700/95 text-white p-4 max-w-md mx-auto">
              <div className="flex items-center justify-center gap-2 mb-2">
                <ArrowsClockwise size={16} weight="bold" className="animate-spin" />
                <p className="text-sm font-black tracking-tight">{funMessage}</p>
              </div>
              <div className="h-1.5 bg-white/20 overflow-hidden">
                <div className="h-full bg-white transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-[10px] text-white/70 mt-1.5">{progress}%</p>
            </div>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="absolute inset-0 bg-emerald-700/95 flex flex-col items-center justify-center text-white px-4">
            <CheckCircle size={48} weight="fill" />
            <p className="text-lg font-black mt-3">{t('scan_success')}</p>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 bg-zinc-950">
            <div className="bg-red-700 text-white p-5 max-w-md w-full text-center">
              <Warning size={32} weight="bold" className="mx-auto mb-2" />
              <p className="text-sm font-black tracking-tight mb-1">{t('scan_error')}</p>
              {errorMsg && <p className="text-xs text-white/85 mt-1.5 leading-snug">{errorMsg}</p>}
            </div>
            <div className="flex flex-col gap-3 items-center">
              <button
                onClick={() => { setStatus('idle'); setErrorMsg(''); }}
                className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-black uppercase tracking-wider px-6 py-3 flex items-center gap-2"
                data-testid="mrz-retry"
              >
                <ArrowsClockwise size={13} weight="bold" /> Riprova
              </button>
              <label className="cursor-pointer bg-white text-zinc-900 hover:bg-violet-50 text-xs font-black uppercase tracking-wider px-6 py-3 transition-colors flex items-center gap-2">
                <ImageSquare size={14} weight="bold" /> Carica foto invece
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => onFilePicked(e.target.files?.[0])}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Bottom action bar */}
      {status === 'streaming' && (
        <div className="bg-black/90 p-4 sm:p-5 flex items-center justify-between gap-3">
          <button
            onClick={() => { stopCamera(); setStatus('idle'); }}
            className="text-white/70 hover:text-white text-xs font-bold uppercase tracking-wider px-4 py-2"
            data-testid="mrz-stop"
          >
            {t('scan_stop')}
          </button>
          <button
            onClick={captureAndRead}
            disabled={status !== 'streaming'}
            className="bg-violet-600 hover:bg-violet-700 text-white font-black uppercase tracking-wider text-sm px-8 py-3.5 disabled:opacity-50 flex items-center gap-2"
            data-testid="mrz-capture"
          >
            <Camera size={16} weight="bold" />
            {t('scan_capture')}
          </button>
        </div>
      )}
    </div>
  );
}
